import type { AulaDaLista, ModuloDoCurso } from './api.js';

/**
 * ── O SIMULADO SAI DA CONTA — DOS DOIS LADOS ───────────────────────────────
 *
 * ## A ordem do dono (24/09/2026)
 *
 * > *«considere que um simulado pulado também não entra para calcular o
 * > rendimento em %… considerar que um curso está concluído com 90% feito, o
 * > simulado não deve entrar na conta, e ao pular ele já entra como concluído,
 * > isso resolve o problema da %»*
 *
 * ## Por que os DOIS lados, e não só o numerador
 *
 * Porque podar um lado só produz um número impossível, e sempre no pior
 * momento: tirar o simulado do numerador e deixá-lo no denominador trava o
 * curso em 90% para quem fez tudo; tirá-lo do denominador e deixá-lo no
 * numerador dá 111% a quem também o fez. A conta é `feitas / contam`, e o que
 * é simulado não entra em nenhuma das duas.
 *
 * ## Por que a tela conta de novo, se o servidor já manda a percentagem
 *
 * Porque são duas contas de coisas diferentes, e as duas aparecem. O servidor
 * manda `progresso.percent` do CURSO (a página do curso e o certificado leem
 * esse); a barra da sala e o anel de cada módulo contam o que está na tela, em
 * tempo real, com a marca optimista de quem acabou de clicar — esperar o
 * servidor faria o disco acender e o anel ficar parado, que é o defeito que
 * `ListaDaSala` já nomeia. O que não pode é as duas discordarem sobre a REGRA:
 * é para isso que esta função existe num sítio só.
 *
 * ## Por que o simulado continua na lista e continua contado À PARTE
 *
 * Porque «9 de 9 aulas» ao lado de dez linhas é a tela a subtrair em silêncio.
 * A frase diz `9 de 9 aulas · 1 simulado`: o que conta, o que existe, e a
 * fronteira entre os dois — sem esconder a linha e sem a somar.
 */

/**
 * `kind: 'quiz'` **e** a espécie de treino. Os dois, sempre.
 *
 * A guarda do `kind` não é zelo: `simulado` chega opcional de um servidor que
 * só o preenche em avaliação, e um `true` que escapasse para outro tipo tiraria
 * uma aula de vídeo da conta do progresso — o defeito mais caro possível, e
 * silencioso.
 */
export function ehSimulado(aula: { kind?: string | null; simulado?: boolean }): boolean {
  return aula.kind === 'quiz' && aula.simulado === true;
}

/** Todas as aulas de um módulo e dos submódulos dele, achatadas. */
export function aulasDoModulo(m: ModuloDoCurso): AulaDaLista[] {
  return [...m.aulas, ...m.submodulos.flatMap(aulasDoModulo)];
}

export interface ContagemDeAulas {
  /** Concluídas que CONTAM — o numerador. */
  feitas: number;
  /** O denominador: as aulas menos os simulados. */
  contam: number;
  /** Os simulados que existem no mesmo escopo, e que não entram em nenhum dos dois. */
  simulados: number;
}

/**
 * A contagem de um escopo — o curso inteiro, um módulo, um submódulo.
 *
 * `concluida` vem de fora porque a lista da sala conta com a marca **optimista**
 * junto (o clique de agora, ainda por gravar) e a barra do curso conta com o
 * que o servidor disse. É a mesma regra sobre dois estados, e não duas regras.
 */
export function contarAulas(
  aulas: readonly AulaDaLista[],
  concluida: (a: AulaDaLista) => boolean = (a) => a.progresso.concluida,
): ContagemDeAulas {
  let feitas = 0;
  let contam = 0;
  let simulados = 0;
  for (const a of aulas) {
    if (ehSimulado(a)) {
      simulados++;
      continue;
    }
    contam++;
    if (concluida(a)) feitas++;
  }
  return { feitas, contam, simulados };
}

/** `feitas / contam` em percentagem inteira. Sem denominador não há conta: 0. */
export function percentagem({ feitas, contam }: ContagemDeAulas): number {
  return contam === 0 ? 0 : Math.round((feitas / contam) * 100);
}

/** «3 de 9 aulas», e o simulado contado à parte — só quando existe um. */
export function fraseDeAulas({ feitas, contam, simulados }: ContagemDeAulas): string {
  const aulas = `${feitas} de ${contam} ${contam === 1 ? 'aula' : 'aulas'}`;
  if (simulados === 0) return aulas;
  return `${aulas} · ${simulados === 1 ? '1 simulado' : `${simulados} simulados`}`;
}

/**
 * Os ids dos simulados do curso, para quem tem a árvore mas não tem a espécie.
 *
 * É o caso do boletim: `avaliacoes` é uma lista à parte, indexada por `aulaId`,
 * e quem sabe a espécie é a árvore de módulos — que a mesma resposta já traz.
 * Derivar daqui é preferível a esperar um campo novo em dois sítios: o dia em
 * que os dois divergissem, a média e o boletim diriam coisas diferentes sobre a
 * mesma prova.
 */
export function idsDeSimulado(modulos: readonly ModuloDoCurso[]): Set<string> {
  const ids = new Set<string>();
  for (const m of modulos) for (const a of aulasDoModulo(m)) if (ehSimulado(a)) ids.add(a.id);
  return ids;
}
